/* eslint-disable  @typescript-eslint/no-explicit-any */
import type {MiddlewarePipeDetails, MiddlewareMisspellingsPipeDetails,} from './models/middleware-pipe-details.model';
import type {PipesRegistry} from './models/stack-pipes-registry.model';

import {makeLogger} from '../../logger/logger';

//explicitly imported middlewares, are compiled statically into this file
import {ApplyFacetOptionsMiddleware} from './pipes/common/middleware-apply-facet-options';
import {callCoseApi} from './pipes/api-calls/call-cose-api';
import {reqResCoseBuildQueryAfterEvents} from './pipes/cose/req-res-cose-build-query-after-events';
import {reqResCoseFacetQueryFacetMapping} from './pipes/cose/req-res-cose-facet-query-facet-mapping';
import {resCoseDocumentsMapping} from './pipes/cose/res-cose-documents-mapping';
import {resCoseFaqDocumentsMapping} from './pipes/cose/res-cose-faq-documents-mapping';
import {resViewItemListEvent} from './pipes/cose/res-view-item-list-event';
import {reqResCoseBuildQueryAfterEventsWithInitialFilters} from './pipes/cose/req-res-cose-build-query-after-events-with-initial-filters';
import {reqResCoseBuildQueryAfterEventsWithProductsFacets} from './pipes/cose/req-res-cose-build-query-after-events-with-products-facets';
import {distributorStockInvalidIds} from './pipes/cose/distributor-stock-invalid-ids';


//others are imported dynamically on first use

const { debug, err } = makeLogger({
  pathToCurrentFile:'utils/middleware-pipe/pipes/middleware-pipe.ts',
  loggersList: ['debug', 'err']
});

const middlewareImportList:Record<string,{file,method:string}> = {
  //It must contain explicit import statements, with explicit 1:1 file names.
  //Otherwise when TS/Stencil transpile the source file, mapping is not updated, and mechanism fails
  //so we must have such a boilerplate :(
  ReqResCoseBuildQueryAfterEventsV2: {
    file: async ()=>await import('./pipes/cose/req-res-cose-build-query-after-events-v2'),
    method: 'reqResCoseBuildQueryAfterEventsV2'
  },
  ReqResCoseBuildQueryAfterEventsWithContext: {
    file: async ()=>await import('./pipes/cose/req-res-cose-build-query-after-events-with-context'),
    method: 'reqResCoseBuildQueryAfterEventsWithContext'
  },
  ReqResCoseBuildQueryAfterEventsWithContextV2: {
    file: async ()=>await import('./pipes/cose/req-res-cose-build-query-after-events-with-context-v2'),
    method: 'reqResCoseBuildQueryAfterEventsWithContextV2'
  },
  ReqResCoseFacetQueryFacetMappingV2: {
    file: async ()=>await import('./pipes/cose/req-res-cose-facet-query-facet-mapping-v2'),
    method: 'reqResCoseFacetQueryFacetMappingV2'
  },
  ReqResCoseSearchableFilterMapping: {
    file: async ()=>await import('./pipes/cose/req-res-cose-searchable-filter-mapping'),
    method: 'reqResCoseSearchableFilterMapping'
  },
  ReqResCoseDateFilterMapping: {
    file: async ()=>await import('./pipes/cose/req-res-cose-date-filter-mapping'),
    method: 'reqResCoseDateFilterMapping'
  },
  ReqResCoseSortingDocuments: {
    file: async ()=>await import('./pipes/cose/req-res-cose-sorting-documents'),
    method: 'reqResCoseSortingDocuments'
  },
  ReqResCoseQuotesStatusesMapping: {
    file: async ()=>await import('./pipes/cose/req-res-cose-quotes-statuses-mapping'),
    method: 'reqResCoseQuotesStatusesMapping'
  },
  ReqResCoseFacetQueryFacetValuesTranslations: {
    file: async ()=>await import('./pipes/cose/req-res-cose-facet-query-facet-values-translations'),
    method: 'reqResCoseFacetQueryFacetValuesTranslations'
  },
  ReqResCoseMapRequestV1ToV2: {
    file: async ()=>await import('./pipes/cose/req-res-cose-map-request-v1-to-v2'),
    method: 'reqResCoseMapRequestV1ToV2'
  },
  ResCoseDocumentsMappingDataGovernance: {
    file: async ()=>await import('./pipes/cose/res-cose-documents-mapping-data-governance'),
    method: 'resCoseDocumentsMappingDataGovernance'
  },
  ResFlatMultiLanguageDocuments: {
    file: async ()=>await import('./pipes/cose/res-flat-multi-language-documents'),
    method: 'resFlatMultiLanguageDocuments'
  },
  ResCoseProductsUrlsMapping: {
    file: async ()=>await import('./pipes/cose/seamless/res-cose-products-urls-mapping'),
    method: 'resCoseProductsUrlsMapping'
  },
  ResSrtEventsEmitting: {
    file: async ()=>await import('./pipes/srt/res-srt-events-emitting'),
    method: 'resSrtEventsEmitting'
  },
  MapUrlsSupportPipe: {
    file: async ()=>await import('./pipes/cose/seamless/res-cose-support-url-mapping'),
    method: 'resCoseSupportUrlMapping'
  },
  ResCoseVideoUrlMapping: {
    file: async ()=>await import('./pipes/cose/res-cose-video-url-mapping'),
    method: 'resCoseVideoUrlMapping'
  },
  ResCoseDataGovToOldMapping: {
    file: async ()=>await import('./pipes/cose/res-cose-data-gov-to-old-mapping'),
    method: 'resCoseDataGovToOldMapping'
  },
  ReqResCoseMobileAppsMapping: {
    file: async ()=>await import('./pipes/cose/req-res-cose-mobile-apps-mapping'),
    method: 'reqResCoseMobileAppsMapping'
  },
  ResCosePriceRangeFacetQueryFacetMapping: {
    file: async ()=>await import('./pipes/cose/res-cose-price-range-facet-query-facet-mapping'),
    method: 'resCosePriceRangeFacetQueryFacetMapping'
  },
  ProductsCategoryI18n: {
    file: async ()=>await import('./pipes/cose/products-category-i18n'),
    method: 'productsCategoryI18n'
  },
  ResCoseDocumentsMappingWithLanguageSwitch: {
    file: async ()=>await import('./pipes/cose/res-cose-documents-mapping-with-language-switch'),
    method: 'resCoseDocumentsMappingWithLanguageSwitch'
  },
  ResCoseExstAllMapping: {
    file: async ()=>await import('./pipes/cose/exchange/res-cose-exst-all-mapping'),
    method: 'resCoseExstAllMapping'
  },
  ResCoseExstTranslate: {
    file: async ()=>await import('./pipes/cose/exchange/res-cose-exst-translate'),
    method: 'resCoseExstTranslate'
  },
  ResECommerceAsyncEnrichment: {
    file: async ()=>await import('./pipes/e-commerce/res-e-commerce-async-enrichment'),
    method: 'resECommerceAsyncEnrichment'
  },
  ReqResCoseProductCatalog: {
    file: async ()=>await import('./pipes/cose/req-res-cose-product-catalog'),
    method: 'reqResCoseProductCatalog'
  },
  ReqResCoseProductNa: {
    file: async ()=>await import('./pipes/cose/req-res-cose-product-na'),
    method: 'reqResCoseProductNaMiddleware'
  },
  FixCharacteristicsFacet: {
    file: async ()=>await import('./pipes/cose/fix-characteristics-facet'),
    method: 'fixCharacteristicsFacet'
  },
  ReqCoseContextCpqQuotesMapping: {
    file: async()=>await import('./pipes/cose/req-cose-context-cpq-quotes-mapping'),
    method: 'reqCoseContextCpqQuotesMapping'
  },
  ClearFiltersAfterLocaleChange:{
    file: async()=>await import('./pipes/cose/clear-filters-after-locale-change'),
    method: 'clearFiltersAfterLocaleChange',
  },
  ResCoseSelectFacets: {
    file: async ()=>await import('./pipes/cose/res-cose-select-facets'),
    method: 'resCoseSelectFacets'
  },
  ResCoseDocumentsSoftwareMapping:{
    file: async()=>await import('./pipes/cose/res-cose-documents-software-mapping'),
    method: 'resCoseDocumentsSoftwareMapping',
  },
  ReqResCoseMappingApcId: {
    file: async()=>await import('./pipes/cose/req-res-cose-mapping-apc-id'),
    method: 'reqResCoseMappingApcId',
  },
  '@callSalesforceOpportunities': {
    file: async ()=>await import('./opportunities/opportunities'),
    method: 'OpportunitiesUiaaSMiddleware'
  },
  applyOpportunityRequest: {
    file: async ()=>await import('./opportunities/prepareRequest'),
    method: 'buildRequestForOpportunity'
  },
  ReqResCoseCircularProductsFacets: {
    file: async ()=>await import('./pipes/cose/req-res-cose-circular-products-facets'),
    method: 'reqResCoseCircularProductsFacets'
  }

};

export type MiddlewareSearchPipe<R> = (details: MiddlewarePipeDetails | MiddlewareMisspellingsPipeDetails) => Promise<R>;

export type PipeMiddlewareElement<IN, OUT> = (
  details: IN,
  next?: undefined | ((n) => any),
  middlewareDepth?: number,
  middlewareArray?: Array<PipeMiddlewareElement<IN, OUT>>
) => Promise<OUT>;

export type PipeMiddlewareNext<OUT> = (
  details: MiddlewarePipeDetails | MiddlewareMisspellingsPipeDetails
) => Promise<Promise<OUT>>;


export const vendorMiddlewares: PipesRegistry = {
  //explicit imports for files to be bundled within the component, not dynamically imported
  '@callCoseApi': callCoseApi,
  applyFacetOptions: ApplyFacetOptionsMiddleware,
  ReqResCoseBuildQueryAfterEvents: reqResCoseBuildQueryAfterEvents, //switch to v2 soon
  ReqResCoseFacetQueryFacetMapping:reqResCoseFacetQueryFacetMapping,
  'ReqResCoseBuildQueryAfterEventsWithProductsFacets': reqResCoseBuildQueryAfterEventsWithProductsFacets,
  ResCoseDocumentsMapping:resCoseDocumentsMapping,
  ResCoseFaqDocumentsMapping:resCoseFaqDocumentsMapping,
  ResViewItemListEvent: resViewItemListEvent,
  DistributorStockInvalidIds: distributorStockInvalidIds,
  ReqResCoseBuildQueryAfterEventsWithInitialFilters: reqResCoseBuildQueryAfterEventsWithInitialFilters,
  //others are dynamically imported on first use
};

const dynamicImportFlags = {};

//dynamicImportFlags['random_id'] = Math.floor(Math.random() * 100000);

for (const name in middlewareImportList) {
  vendorMiddlewares[name] = async (...args) => {
      //debug('using middleware import container',dynamicImportFlags['random_id']);
      const middleware = middlewareImportList[name];
      try {
        if(!dynamicImportFlags[name]){
          debug('Dynamic import for middleware', name, middleware);
          const {file, method} = middleware;
          const call = (await file())[method];
          dynamicImportFlags[name] = call;
          return await call(...args);
        }else{
          debug('Reuse imported middleware', name, dynamicImportFlags[name]);
          return await dynamicImportFlags[name](...args);
        }
      } catch (e) {
        err('Middleware dynamic import failed', e, {
          name,
          middleware,
        });
      }
  };
}

const noopMiddleware = (name) => async (details, next) => {
    const response = await next(details);
    return new Promise(r => r(response));
};

export class MiddlewareEscape<R> extends Error {
  returnedObject?: R;
}

export async function middlewarePipe<R>(
  details: MiddlewarePipeDetails | MiddlewareMisspellingsPipeDetails
): Promise<R> {
    const userMiddlewares: PipesRegistry = await details.context.getMiddlewareRegistry();
    const stackAsString: string = (details.config.customMiddlewares) ? details.config.customMiddlewares : details.context.middlewares;

    const stack = stackAsString
      .split('|')
      .map(name => userMiddlewares[name] || vendorMiddlewares[name] || noopMiddleware(name));

    debug('I HAVE STACK PIPE_MIDDLEWARE', stack);
    try {
      const re = async (details: MiddlewarePipeDetails | MiddlewareMisspellingsPipeDetails): Promise<R> => {
          const middlewares = stack.reverse();

          return middlewares.reduce(
            (
              previousValue: PipeMiddlewareElement<any, any>, //accumulator
              currentValue: PipeMiddlewareElement<any, any>, //current
              middlewareDepth, // deepest = 0, leftmost = max number (last array index)
              middlewareArray// probably has no use,
              // we could break array.reduce by changing the array, however in our case we first create the call chain,
              // then execute it - so there is no occasion to alter the array when control is within the middleware
              // but we keep the value - to be consistent with array.reduce signature
            ) => {

                // if(details.config?.skipOn?.middlewareFunctionName === currentValue.name && details.config?.skipOn?.middlewareDepth === middlewareDepth) {
                //   middlewareArray.splice(middlewareDepth, 1);
                //   console.log('orginal slice', middlewares, middlewareArray);
                //   return async (d: MiddlewarePipeDetails | MiddlewareMisspellingsPipeDetails) => { // <= arguments for the next(details) function
                //       //values are passed to each middleware
                //       return await currentValue({...d}, previousValue, middlewareDepth, middlewareArray);
                //   };
                // }

                //this function is the next(details) within the middleware
                return async (d: MiddlewarePipeDetails | MiddlewareMisspellingsPipeDetails) => { // <= arguments for the next(details) function
                    //values are passed to each middleware
                    return await currentValue({...d}, previousValue, middlewareDepth, middlewareArray);
                };
            }, (d) => d
          )(details);
      };

      return await re(details);

    } catch (e) {
      if (e instanceof MiddlewareEscape) {
      //MiddlewareEscape is used to exit the middleware chain, not letting other middlewares to run
      //however data can be passed back to the caller with e.returnedObject field;
        if (e.returnedObject) {
          return e.returnedObject;
        } else return;
      } else {
      //in case of any other exception, we pass it back to the caller,
      //so that it can be handled by the caller. Like it should be handled when network error occurs.
        throw e;
      }
    }

}
